import { createSVGElement } from '@/utils/svg-kit';
import type { BoundingBox } from '@/version-types';

/**
 * 创建用户空间高斯模糊滤镜。
 *
 * @param id - 滤镜唯一 ID。
 * @param box - 变换前源图形允许参与滤镜计算的边界。
 * @param blur - Figma 面板中的模糊值；SVG 标准差为该值的一半。
 * @returns 高斯模糊滤镜节点。
 */
export const createBlurFilter = (id: string, box: BoundingBox, blur: number): SVGFilterElement => {
    const filter = createSVGElement('filter', {
        id,
        x: box.left,
        y: box.top,
        width: box.width,
        height: box.height,
        filterUnits: 'userSpaceOnUse',
        'color-interpolation-filters': 'sRGB',
    });
    const gaussianBlur = createSVGElement('feGaussianBlur', {
        in: 'SourceGraphic',
        stdDeviation: blur / 2,
    });

    filter.appendChild(gaussianBlur);

    return filter;
};

/**
 * 创建只输出着色投影的滤镜。
 *
 * 偏移和模糊参数沿用 Figma 语义；主体由独立 use 后绘制，因此阴影必须保留主体下方的 alpha，
 * 避免主体边缘或主体模糊区域透出画布。
 *
 * @param id - 滤镜唯一 ID。
 * @param options - 投影滤镜配置。
 * @returns 阴影滤镜节点。
 */
export const createShadowFilter = (
    id: string,
    {
        box,
        x,
        y,
        blur,
        color,
        opacity,
    }: {
        box: BoundingBox;
        x: number;
        y: number;
        blur: number;
        color: string;
        opacity: number;
    },
): SVGFilterElement => {
    const filter = createSVGElement('filter', {
        id,
        x: box.left,
        y: box.top,
        width: box.width,
        height: box.height,
        filterUnits: 'userSpaceOnUse',
        'color-interpolation-filters': 'sRGB',
    });
    const hardAlpha = createSVGElement('feColorMatrix', {
        in: 'SourceAlpha',
        type: 'matrix',
        values: '0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0',
        result: 'hard-alpha',
    });
    const offset = createSVGElement('feOffset', {
        in: 'hard-alpha',
        dx: x,
        dy: y,
        result: 'shadow-offset',
    });
    const gaussianBlur = createSVGElement('feGaussianBlur', {
        in: 'shadow-offset',
        stdDeviation: blur / 2,
        result: 'shadow-blur',
    });
    const flood = createSVGElement('feFlood', {
        'flood-color': color,
        'flood-opacity': opacity,
        result: 'shadow-color',
    });
    const composite = createSVGElement('feComposite', {
        in: 'shadow-color',
        in2: 'shadow-blur',
        operator: 'in',
    });

    filter.appendChild(hardAlpha);
    filter.appendChild(offset);
    filter.appendChild(gaussianBlur);
    filter.appendChild(flood);
    filter.appendChild(composite);

    return filter;
};
